import React, { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import PageHeader from '../components/PageHeader'
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts'

const DEMO_METRICS = {
  revenueThisMonth: 0,
  pendingThisMonth: 0,
  overdueAmount: 0,
  targetAchieved: 0,
  pipelineValue: 0,
  pipelineActive: 0,
  target: 0,
}

const DEMO_PROJECTS = []

const DEMO_BILLING = []

function stageBadge(stage) {
  const map = {
    'Running':'badge badge-green', 'In Progress':'badge badge-blue',
    'Not Started':'badge badge-gray', 'Paused':'badge badge-amber', 'Stopped':'badge badge-red',
    'Review':'badge badge-amber', 'Planning':'badge badge-purple',
    'Requirement':'badge badge-gray', 'Completed':'badge badge-green', 'On Hold':'badge badge-red'
  }
  return map[stage] || 'badge badge-gray'
}

function payBadge(status) {
  const map = { Paid:'badge badge-green', Received:'badge badge-green', Pending:'badge badge-amber', Overdue:'badge badge-red', Waived:'badge badge-gray' }
  return map[status] || 'badge badge-amber'
}

function fmt(n) { return '₹' + Number(n).toLocaleString('en-IN') }

const CHART_COLORS = { paid:'#166534', pending:'#92400E', overdue:'#991B1B' }
const PIE_COLORS = ['#166534', '#92400E', '#991B1B', '#6B6860']

export default function Dashboard() {
  const [metrics, setMetrics] = useState(DEMO_METRICS)
  const [projects, setProjects] = useState(DEMO_PROJECTS)
  const [billing, setBilling] = useState(DEMO_BILLING)
  const [revenueTrend, setRevenueTrend] = useState([])
  const [pipelineFunnel, setPipelineFunnel] = useState([])
  const [billingBreakdown, setBillingBreakdown] = useState([])
  const [alerts, setAlerts] = useState([])
  const [targetModal, setTargetModal] = useState(false)
  const [targetInput, setTargetInput] = useState('')
  const [savingTarget, setSavingTarget] = useState(false)

  useEffect(() => {
    async function load() {
      const [svcRes, msRes, pipeRes, billRes, settingsRes, cRes, bRes] = await Promise.all([
        supabase.from('services').select('*').not('status', 'in', '("Paused","Stopped")'),
        supabase.from('milestones').select('*'),
        supabase.from('pipeline').select('*'),
        supabase.from('billing_records').select('*').order('billing_date', { ascending: false }),
        supabase.from('settings').select('value').eq('key', 'monthly_target').single(),
        supabase.from('clients').select('id, name'),
        supabase.from('brands').select('id, name'),
      ])
      const fetchedTarget = Number(settingsRes.data?.value) || 200000

      const svcAll = svcRes.data || []
      const allMilestones = msRes.data || []
      const pipe = pipeRes.data || []
      const bills = billRes.data || []
      const cm = {}; (cRes.data || []).forEach(c => { cm[c.id] = c.name })
      const bm = {}; (bRes.data || []).forEach(b => { bm[b.id] = b.name })

      // Enrich services
      const svc = svcAll.map(s => ({
        ...s,
        client_name: cm[s.client_id] || '—',
        brand_name: bm[s.brand_id] || '—',
        milestones: allMilestones.filter(m => m.service_id === s.id),
      }))
      const mkt = svc.filter(s => s.service_type === 'Marketing')
      const dev = svc.filter(s => s.service_type === 'Development')
      const hosting = svc.filter(s => s.service_type === 'Hosting')

      if (svc.length || pipe.length || bills.length) {
        // Revenue from billing_records this month
        const now = new Date()
        const thisMonth = now.toISOString().slice(0, 7)
        const monthBills = bills.filter(b => b.billing_date?.startsWith(thisMonth))
        const paidAmount = monthBills.filter(b => b.status === 'Paid').reduce((s, b) => s + (Number(b.amount) || 0), 0)
        const pendingAmount = monthBills.filter(b => b.status === 'Pending').reduce((s, b) => s + (Number(b.amount) || 0), 0)
        const overdueAmount = monthBills.filter(b => b.status === 'Overdue').reduce((s, b) => s + (Number(b.amount) || 0), 0)

        // Fallback: compute from services if no billing records
        const svcReceived = svc.filter(s => s.payment_status === 'Received').reduce((sum, s) => sum + (Number(s.value) || 0), 0)
        const msReceived = allMilestones.filter(m => m.payment_status === 'Received').reduce((sum, m) => sum + (Number(m.milestone_amount) || 0), 0)
        const revenue = bills.length > 0 ? paidAmount : (svcReceived + msReceived)

        const svcPending = svc.filter(s => ['Pending', 'Overdue'].includes(s.payment_status)).reduce((sum, s) => sum + (Number(s.value) || 0), 0)
        const msPending = allMilestones.filter(m => ['Pending', 'Overdue'].includes(m.payment_status)).reduce((sum, m) => sum + (Number(m.milestone_amount) || 0), 0)
        const pending = bills.length > 0 ? (pendingAmount + overdueAmount) : (svcPending + msPending)

        const pipelineValue = pipe.filter(l => !['Won', 'Lost'].includes(l.stage)).reduce((s, l) => s + (Number(l.estimated_value) || 0), 0)
        const pipelineActive = pipe.filter(l => !['Won', 'Lost'].includes(l.stage)).length

        const target = fetchedTarget
        const pct = target > 0 ? Math.round((revenue / target) * 100) : 0

        setMetrics({ revenueThisMonth: revenue, pendingThisMonth: pending, overdueAmount, targetAchieved: pct, pipelineValue, pipelineActive, target })

        // Build alerts
        const todayStr = now.toISOString().slice(0, 10)
        const tomorrowStr = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).toISOString().slice(0, 10)
        const in30 = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 30).toISOString().slice(0, 10)
        const alertList = []

        // Marketing payment alerts
        const mktOverdue = mkt.filter(p => p.payment_status !== 'Received' && p.next_billing_date && p.next_billing_date < todayStr)
        if (mktOverdue.length > 0) alertList.push({ type: 'danger', text: `${mktOverdue.length} marketing payment${mktOverdue.length > 1 ? 's' : ''} overdue`, detail: mktOverdue.map(p => p.client_name).join(', ') })
        const mktDueToday = mkt.filter(p => p.payment_status !== 'Received' && p.next_billing_date === todayStr)
        if (mktDueToday.length > 0) alertList.push({ type: 'warning', text: `${mktDueToday.length} marketing payment${mktDueToday.length > 1 ? 's' : ''} due today`, detail: mktDueToday.map(p => p.client_name).join(', ') })
        const mktDueTomorrow = mkt.filter(p => p.payment_status !== 'Received' && p.next_billing_date === tomorrowStr)
        if (mktDueTomorrow.length > 0) alertList.push({ type: 'info', text: `${mktDueTomorrow.length} marketing payment${mktDueTomorrow.length > 1 ? 's' : ''} due tomorrow`, detail: mktDueTomorrow.map(p => p.client_name).join(', ') })

        // Dev milestone alerts
        const devOverdue = allMilestones.filter(m => {
          const s = dev.find(d => d.id === m.service_id)
          return s && m.payment_status !== 'Received' && m.due_date && m.due_date < todayStr
        })
        if (devOverdue.length > 0) alertList.push({ type: 'danger', text: `${devOverdue.length} dev milestone${devOverdue.length > 1 ? 's' : ''} overdue`, detail: [...new Set(devOverdue.map(m => { const s = dev.find(d => d.id === m.service_id); return s?.brand_name || '—' }))].join(', ') })

        // Hosting renewal alerts (< 30 days)
        const hostingRenewing = hosting.filter(h => h.domain_renewal_date && h.domain_renewal_date <= in30)
        if (hostingRenewing.length > 0) alertList.push({ type: 'warning', text: `${hostingRenewing.length} domain${hostingRenewing.length > 1 ? 's' : ''} renewing within 30 days`, detail: hostingRenewing.map(h => (h.domain || h.brand_name) + ' (' + h.domain_renewal_date + ')').join(', ') })

        // Pipeline alerts
        const followUpOverdue = pipe.filter(l => !['Won','Lost'].includes(l.stage) && l.follow_up_date && l.follow_up_date < todayStr)
        if (followUpOverdue.length > 0) alertList.push({ type: 'warning', text: `${followUpOverdue.length} follow-up${followUpOverdue.length > 1 ? 's' : ''} overdue in pipeline`, detail: followUpOverdue.map(l => l.client_name).join(', ') })
        const reqOverdue = pipe.filter(l => !['Won','Lost'].includes(l.stage) && l.requirements && !l.requirements_done && l.requirements_due && l.requirements_due < todayStr)
        if (reqOverdue.length > 0) alertList.push({ type: 'warning', text: `${reqOverdue.length} pipeline requirement${reqOverdue.length > 1 ? 's' : ''} overdue`, detail: reqOverdue.map(l => l.client_name).join(', ') })
        setAlerts(alertList)

        // Build projects list from services
        const projList = svc.map(s => ({
          client: s.client_name,
          project: s.brand_name,
          stage: s.status,
          assigned: s.assigned_to,
          type: s.service_type.toLowerCase(),
        }))
        if (projList.length) setProjects(projList)

        // Billing table from billing_records (this month, non-paid)
        const pendingBills = monthBills.filter(b => b.status !== 'Paid').map(b => ({
          client: b.description?.split(' — ')[0] || '—', amount: Number(b.amount) || 0, due: b.due_date || b.billing_date, status: b.status, description: b.description || ''
        }))
        // Fallback to project tables if no billing_records
        if (pendingBills.length) {
          pendingBills.sort((a, b) => b.amount - a.amount)
          setBilling(pendingBills)
        } else if (!bills.length) {
          const billList = []
          mkt.filter(s => s.payment_status !== 'Received').forEach(s => billList.push({ client: s.client_name, amount: Number(s.value) || 0, due: s.next_billing_date || '—', status: s.payment_status, description: s.brand_name }))
          allMilestones.filter(m => m.payment_status !== 'Received').forEach(m => {
            const s = dev.find(d => d.id === m.service_id)
            if (s) billList.push({ client: s.client_name, amount: Number(m.milestone_amount) || 0, due: m.due_date || '—', status: m.payment_status, description: s.brand_name + ' — ' + m.milestone_name })
          })
          billList.sort((a, b) => b.amount - a.amount)
          if (billList.length) setBilling(billList)
        }

        // Revenue trend (last 6 months from billing_records)
        if (bills.length) {
          const months = []
          for (let i = 5; i >= 0; i--) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
            const key = d.toISOString().slice(0, 7)
            const label = d.toLocaleDateString('en-IN', { month: 'short' })
            const paid = bills.filter(b => b.status === 'Paid' && b.billing_date?.startsWith(key)).reduce((s, b) => s + (Number(b.amount) || 0), 0)
            months.push({ month: label, revenue: paid })
          }
          setRevenueTrend(months)
        }

        // Pipeline funnel
        const stages = ['New Lead', 'Contacted', 'Proposal Sent', 'Negotiation', 'Won', 'Lost']
        const funnel = stages.map(s => ({ stage: s.replace(' ', '\n'), count: pipe.filter(l => l.stage === s).length })).filter(f => f.count > 0)
        if (funnel.length) setPipelineFunnel(funnel)

        // Billing breakdown pie (this month)
        const breakdown = [
          { name: 'Paid', value: paidAmount },
          { name: 'Pending', value: pendingAmount },
          { name: 'Overdue', value: overdueAmount },
        ].filter(b => b.value > 0)
        if (breakdown.length) setBillingBreakdown(breakdown)
      }
    }
    load()
  }, [])

  const today = new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
  const m = metrics
  const pct = Math.min(100, m.targetAchieved)

  return (
    <div style={{ display:'flex', flexDirection:'column', flex:1 }}>
      <PageHeader title="Dashboard" subtitle={today} />
      <div style={{ padding: 28, flex: 1 }}>

        {alerts.length > 0 && (
          <div style={{ marginBottom:20, display:'flex', flexDirection:'column', gap:6 }}>
            {alerts.map((a, i) => {
              const colors = {
                danger: { bg:'var(--danger-bg)', border:'var(--danger)', text:'var(--danger)' },
                warning: { bg:'var(--warning-bg)', border:'var(--warning)', text:'var(--warning)' },
                info: { bg:'var(--accent-bg)', border:'var(--accent)', text:'var(--accent)' },
              }
              const c = colors[a.type]
              return (
                <div key={i} style={{ display:'flex', alignItems:'baseline', gap:10, background:c.bg, border:`1px solid ${c.border}`, borderRadius:8, padding:'8px 14px', fontSize:13 }}>
                  <span style={{ fontWeight:600, color:c.text, whiteSpace:'nowrap' }}>{a.text}</span>
                  <span style={{ color:'var(--text2)', fontSize:12 }}>{a.detail}</span>
                </div>
              )
            })}
          </div>
        )}

        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:28 }}>
          {[
            { label: 'Revenue received', value: fmt(m.revenueThisMonth), sub: 'This month', subColor: 'var(--success)' },
            { label: 'Pending payments', value: fmt(m.pendingThisMonth), sub: 'Awaiting payment', subColor: 'var(--warning)' },
            { label: 'Target achieved', value: m.targetAchieved + '%', sub: 'Target: ' + fmt(m.target), subColor: m.targetAchieved >= 100 ? 'var(--success)' : 'var(--text3)' },
            { label: 'Pipeline value', value: fmt(m.pipelineValue), sub: (m.pipelineActive || 0) + ' leads in progress', subColor: 'var(--text3)' },
          ].map((card, i) => (
            <div key={i} style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:10, padding:'16px 18px', boxShadow:'var(--shadow)' }}>
              <div style={{ fontSize:11, fontWeight:600, color:'var(--text3)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:8 }}>{card.label}</div>
              <div style={{ fontSize:22, fontWeight:600, color:'var(--text)', fontVariantNumeric:'tabular-nums' }}>{card.value}</div>
              <div style={{ fontSize:11, color:card.subColor, marginTop:5 }}>{card.sub}</div>
            </div>
          ))}
        </div>

        <div style={{ marginBottom:20 }}>
          <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:10, padding:'16px 18px', boxShadow:'var(--shadow)' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
              <span style={{ fontSize:13, fontWeight:500 }}>Monthly revenue target</span>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <span style={{ fontSize:13, color: pct >= 100 ? 'var(--success)' : 'var(--warning)' }}>{m.targetAchieved}% of {fmt(m.target)}</span>
                <button className="btn" style={{ padding:'3px 10px', fontSize:11 }} onClick={() => { setTargetInput(String(m.target)); setTargetModal(true) }}>Edit target</button>
              </div>
            </div>
            <div style={{ height:8, background:'var(--surface2)', borderRadius:4, overflow:'hidden' }}>
              <div style={{ height:'100%', width: pct+'%', background: pct >= 100 ? '#166534' : 'var(--accent)', borderRadius:4, transition:'width 0.6s ease' }} />
            </div>
          </div>
        </div>

        {targetModal && (
          <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setTargetModal(false)}>
            <div className="modal" style={{ maxWidth:360 }}>
              <div className="modal-header">
                <h3>Set monthly target</h3>
                <button className="modal-close" onClick={() => setTargetModal(false)}>×</button>
              </div>
              <div className="modal-body">
                <div className="form-field">
                  <label>Target amount (₹)</label>
                  <input type="number" value={targetInput} onChange={e => setTargetInput(e.target.value)} placeholder="e.g. 200000" autoFocus />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn" onClick={() => setTargetModal(false)}>Cancel</button>
                <button className="btn btn-primary" disabled={savingTarget || !targetInput} onClick={async () => {
                  setSavingTarget(true)
                  await supabase.from('settings').upsert({ key: 'monthly_target', value: String(Number(targetInput)) })
                  const newTarget = Number(targetInput) || 200000
                  const newPct = newTarget > 0 ? Math.round((m.revenueThisMonth / newTarget) * 100) : 0
                  setMetrics(prev => ({ ...prev, target: newTarget, targetAchieved: newPct }))
                  setSavingTarget(false)
                  setTargetModal(false)
                }}>
                  {savingTarget ? 'Saving…' : 'Save target'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Charts row */}
        <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:20, marginBottom:28 }}>
          {revenueTrend.length > 0 && (
            <div className="card" style={{ padding:20 }}>
              <div style={{ fontSize:13, fontWeight:600, marginBottom:16 }}>Revenue trend (last 6 months)</div>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={revenueTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                  <XAxis dataKey="month" tick={{ fontSize:11, fill:'#6B6860' }} />
                  <YAxis tick={{ fontSize:11, fill:'#6B6860' }} tickFormatter={v => '₹' + (v/1000) + 'k'} />
                  <Tooltip formatter={v => fmt(v)} labelStyle={{ fontSize:12 }} />
                  <Area type="monotone" dataKey="revenue" stroke="#1B4FD8" fill="#EEF2FF" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {billingBreakdown.length > 0 && (
            <div className="card" style={{ padding:20 }}>
              <div style={{ fontSize:13, fontWeight:600, marginBottom:16 }}>Billing this month</div>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={billingBreakdown} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value">
                    {billingBreakdown.map((entry, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={v => fmt(v)} />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ display:'flex', justifyContent:'center', gap:16, marginTop:8 }}>
                {billingBreakdown.map((b, i) => (
                  <div key={i} style={{ display:'flex', alignItems:'center', gap:6, fontSize:11 }}>
                    <div style={{ width:8, height:8, borderRadius:2, background:PIE_COLORS[i % PIE_COLORS.length] }} />
                    <span style={{ color:'var(--text2)' }}>{b.name}: {fmt(b.value)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {revenueTrend.length === 0 && billingBreakdown.length === 0 && (
            <div className="card" style={{ padding:40, textAlign:'center', color:'var(--text3)', gridColumn:'1 / -1' }}>
              Charts will appear once billing records are generated
            </div>
          )}
        </div>

        {/* Pipeline funnel */}
        {pipelineFunnel.length > 0 && (
          <div className="card" style={{ padding:20, marginBottom:28 }}>
            <div style={{ fontSize:13, fontWeight:600, marginBottom:16 }}>Pipeline funnel</div>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={pipelineFunnel} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis type="number" tick={{ fontSize:11, fill:'#6B6860' }} allowDecimals={false} />
                <YAxis type="category" dataKey="stage" tick={{ fontSize:11, fill:'#6B6860' }} width={90} />
                <Tooltip />
                <Bar dataKey="count" fill="#1B4FD8" radius={[0, 4, 4, 0]} barSize={20} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
          <div>
            <div style={{ fontSize:13, fontWeight:600, marginBottom:12, color:'var(--text)' }}>Active projects ({projects.length})</div>
            <div className="card">
              <table>
                <thead><tr><th>Client</th><th>Project</th><th>Stage</th><th>Assigned</th></tr></thead>
                <tbody>
                  {projects.map((p, i) => (
                    <tr key={i}>
                      <td style={{ fontWeight:500 }}>{p.client}</td>
                      <td style={{ color:'var(--text2)' }}>{p.project}</td>
                      <td><span className={stageBadge(p.stage)}>{p.stage}</span></td>
                      <td>{p.assigned}</td>
                    </tr>
                  ))}
                  {projects.length === 0 && <tr><td colSpan={4} style={{ textAlign:'center', color:'var(--text3)', padding:20 }}>No active projects</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <div style={{ fontSize:13, fontWeight:600, marginBottom:12, color:'var(--text)' }}>Pending billing ({billing.length})</div>
            <div className="card">
              <table>
                <thead><tr><th>Client</th><th>Amount</th><th>Due</th><th>Status</th></tr></thead>
                <tbody>
                  {billing.map((b, i) => (
                    <tr key={i}>
                      <td style={{ fontWeight:500 }}>{b.client}</td>
                      <td style={{ fontVariantNumeric:'tabular-nums' }}>{fmt(b.amount)}</td>
                      <td style={{ color:'var(--text2)' }}>{b.due}</td>
                      <td><span className={payBadge(b.status)}>{b.status}</span></td>
                    </tr>
                  ))}
                  {billing.length === 0 && <tr><td colSpan={4} style={{ textAlign:'center', color:'var(--text3)', padding:20 }}>No pending billing</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>

      </div>
    </div>
  )
}
